import React from "react";
import { Link } from "react-router-dom";
import { company, mapsUrl, whatsappUrl } from "../data/companyData";
import ContactForm from "../components/ContactForm";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { breadcrumbSchema, pageMeta } from "../data/seoData";
import "./Contact.css";

const CHANNELS = [
  {
    icon: "fa-brands fa-whatsapp",
    label: "WhatsApp",
    value: company.phones.whatsapp.display,
    href: whatsappUrl,
    external: true,
    hint: "Fastest for quick specs",
  },
  {
    icon: "fas fa-phone",
    label: "Office",
    value: company.phones.office.display,
    href: `tel:${company.phones.office.tel}`,
    hint: "Mon-Sat, 10:00-19:00 IST",
  },
  {
    icon: "fas fa-phone-volume",
    label: "Landline",
    value: company.phones.landline.display,
    href: `tel:${company.phones.landline.tel}`,
    hint: "Office hours",
  },
  {
    icon: "fas fa-envelope",
    label: "Email",
    value: company.email,
    href: `mailto:${company.email}`,
    hint: "Attach your specification",
  },
];

const LOGISTICS = [
  { label: "Dispatch", items: company.shipping },
  { label: "Payment", items: company.payments },
  { label: "Banking", items: company.banks },
];

const Contact = () => (
  <>
    <Seo
      {...pageMeta.contact}
      schemas={[["breadcrumb", breadcrumbSchema([{ name: "Contact", path: "/contact" }])]]}
    />
    <header className="phero">
      <div className="phero__glow" />
      <div className="phero__grid" />
      <div className="shell phero__inner">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span>Contact</span>
        </nav>

        <Reveal as="h1" className="phero__title" variant="mask">
          Let’s talk specifications.
        </Reveal>
        <Reveal as="p" className="phero__lead" delay={0.1}>
          Call, message or send the form. Give us the compound, grade, quantity
          and destination and you will have a landed price within one working
          day.
        </Reveal>
      </div>
    </header>

    <section className="section on-dark">
      <div className="shell contact__grid">
        <Reveal className="contact__form-col">
          <ContactForm />
        </Reveal>

        <div className="contact__side">
          <Reveal>
            <p className="eyebrow eyebrow--dark">Direct channels</p>
            <ul className="chan">
              {CHANNELS.map((c, i) => (
                <li key={c.label}>
                  <a
                    href={c.href}
                    className="chan__row"
                    target={c.external ? "_blank" : undefined}
                    rel={c.external ? "noreferrer" : undefined}>
                    <span className="chan__icon">
                      <i className={c.icon} aria-hidden="true" />
                    </span>
                    <span className="chan__text">
                      <span className="chan__label">{c.label}</span>
                      <span className="chan__value">{c.value}</span>
                      <span className="chan__hint">{c.hint}</span>
                    </span>
                    <span className="chan__go" aria-hidden="true">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="eyebrow eyebrow--dark contact__gap">Registered office</p>
            <address className="contact__addr">
              {company.address.lines.map((line) => (
                <span key={line}>{line}</span>
              ))}
              <span>{company.address.country}</span>
            </address>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="link contact__map-link">
              Open in Maps
              <span className="btn__arrow" aria-hidden="true">→</span>
            </a>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="eyebrow eyebrow--dark contact__gap">Office hours</p>
            <ul className="contact__hours">
              {company.hours.map((h) => (
                <li key={h.days}>
                  <span>{h.days}</span>
                  <span className="mono">{h.time}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="eyebrow eyebrow--dark contact__gap">Registration</p>
            <p className="contact__gst mono">GST {company.gst}</p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section--tight on-ink-1 contact__logistics">
      <div className="shell">
        <div className="contact__log-grid">
          {LOGISTICS.map((group, i) => (
            <Reveal key={group.label} delay={i * 0.06}>
              <p className="eyebrow eyebrow--dark">{group.label}</p>
              <ul className="contact__log-list">
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="contact__map">
      <iframe
        title={`${company.name} location map`}
        src={`https://www.google.com/maps?q=${company.address.mapsQuery}&output=embed`}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
      <div className="contact__map-card">
        <p className="eyebrow eyebrow--dark">Find us</p>
        <h3>{company.address.city}, {company.address.state}</h3>
        <p>{company.address.lines.join(", ")}</p>
        <a href={mapsUrl} target="_blank" rel="noreferrer" className="btn btn--accent btn--sm">
          Directions
          <span className="btn__arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  </>
);

export default Contact;
